import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import {
  Save,
  Loader2,
  Eye,
  Type,
  Link2,
  Tag,
  Tags,
  Upload,
  X,
} from "lucide-react";

import api from "../../lib/api";
import AdminLayout from "../../components/admin/AdminLayout";
import RichTextEditor from "../../components/admin/RichTextEditor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Escape plain text before injecting into the preview window markup.
const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// Quill emits "<p><br></p>" for an empty editor — treat that (and any
// whitespace-only / tag-only markup) as no content.
const isEmptyHtml = (html) => {
  if (!html) return true;
  if (/<(img|iframe|video)/i.test(html)) return false;
  return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, "").trim() === "";
};

// Blog categories available in the dropdown.
const CATEGORIES = ["News", "Tech", "Events","Education" , "Builder Series","Growth Series"];

// Field length standards.
const TITLE_MIN = 3;
const TITLE_MAX = 150;
const SLUG_MAX = 160;
const TAG_MAX = 25;
const TAGS_MAX = 10;

// Turn a title into a URL-friendly slug.
const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

// Small uppercase label with a leading icon — the "Details" style.
const FieldLabel = ({ icon: Icon, children, htmlFor }) => (
  <label
    htmlFor={htmlFor}
    className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400"
  >
    {Icon && <Icon className="h-3.5 w-3.5" />}
    {children}
  </label>
);

const fieldClass =
  "w-full rounded-md border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#b32126] focus:bg-white focus:ring-2 focus:ring-[#b32126]/15 focus:outline-none transition";

// Contained card with an inline title.
const Card = ({ title, children }) => (
  <section className="bg-white rounded-md border border-gray-200 shadow-sm p-5 sm:p-6">
    <h2 className="text-sm font-semibold text-gray-900 mb-5">{title}</h2>
    {children}
  </section>
);

const BlogPageAdd = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  // Track whether the user typed their own slug so we stop auto-syncing it.
  const [slugTouched, setSlugTouched] = useState(false);
  const [tagInput, setTagInput] = useState("");
  // Data URL used only to render the chosen image; the File lives in formData.
  const [imagePreview, setImagePreview] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    category: "",
    content: "",
    tags: [],
    image: null, // uploaded File
    imageUrl: "", // OR an external image URL — both map to blog_image
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      // Keep the slug in sync with the title until the user edits it manually.
      if (name === "title" && !slugTouched) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const handleSlugChange = (e) => {
    setSlugTouched(true);
    setFormData((prev) => ({ ...prev, slug: slugify(e.target.value) }));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      // Choosing a file clears any URL — they're mutually exclusive.
      setFormData((prev) => ({ ...prev, image: file, imageUrl: "" }));
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    // Typing a URL clears any uploaded file — mutually exclusive.
    setFormData((prev) => ({ ...prev, imageUrl: url, image: null }));
    setImagePreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Clearing either input brings both fields back.
  const removeImage = () => {
    setFormData((prev) => ({ ...prev, image: null, imageUrl: "" }));
    setImagePreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const addTag = (raw) => {
    const tag = raw.trim().replace(/,$/, "");
    if (!tag) return;
    if (tag.length > TAG_MAX) {
      toast.error(`Each tag must be ${TAG_MAX} characters or less`);
      return;
    }
    if (formData.tags.includes(tag)) {
      setTagInput("");
      return;
    }
    if (formData.tags.length >= TAGS_MAX) {
      toast.error(`You can add up to ${TAGS_MAX} tags`);
      return;
    }
    setFormData((prev) => ({ ...prev, tags: [...prev.tags, tag] }));
    setTagInput("");
  };

  const removeTag = (tag) =>
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(tagInput);
    } else if (e.key === "Backspace" && !tagInput && formData.tags.length) {
      removeTag(formData.tags[formData.tags.length - 1]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Front-end validation — show one error at a time.
    const title = formData.title.trim();
    if (!title) {
      toast.error("Title is required");
      return;
    }
    if (title.length < TITLE_MIN) {
      toast.error(`Title must be at least ${TITLE_MIN} characters`);
      return;
    }
    if (title.length > TITLE_MAX) {
      toast.error(`Title must be ${TITLE_MAX} characters or less`);
      return;
    }
    if (!formData.category.trim()) {
      toast.error("Category is required");
      return;
    }
    if (!formData.image && !formData.imageUrl.trim()) {
      toast.error("Featured image (upload or URL) is required");
      return;
    }
    if (formData.slug.length > SLUG_MAX) {
      toast.error(`Slug must be ${SLUG_MAX} characters or less`);
      return;
    }
    if (formData.tags.some((t) => t.length > TAG_MAX)) {
      toast.error(`Each tag must be ${TAG_MAX} characters or less`);
      return;
    }
    if (formData.tags.length > TAGS_MAX) {
      toast.error(`You can add up to ${TAGS_MAX} tags`);
      return;
    }

    setSaving(true);

    // The image is a File, so send everything as multipart/form-data.
    const data = new FormData();
    data.append("blog_title", formData.title);
    data.append("blog_slug", formData.slug);
    data.append("blog_category", formData.category);
    if (!isEmptyHtml(formData.content)) {
      data.append("blog_description", formData.content);
    }
    formData.tags.forEach((tag) => data.append("blog_tags[]", tag));
    // Both the uploaded file and the URL save to the same blog_image field.
    if (formData.image) {
      data.append("blog_image", formData.image);
    } else if (formData.imageUrl.trim()) {
      data.append("blog_image", formData.imageUrl.trim());
    }

    try {
      const response = await api.post("/blogs/add", data);
      toast.success(response.data?.message || "Blog saved successfully");
      navigate("/admin/blogs");
    } catch (error) {
      const result = error.response?.data;
      if (result?.errors) {
        // Show just the first validation error.
        const firstError = Object.values(result.errors)[0];
        toast.error(firstError?.[0] || "Failed to save blog");
      } else {
        toast.error(result?.message || "Failed to save blog");
      }
    } finally {
      setSaving(false);
    }
  };

  const hasPreview =
    formData.title || formData.content || imagePreview || formData.tags.length;

  // Open the article in a new tab styled to match the public detail page.
  const handlePreview = () => {
    const win = window.open("", "blogPreview");
    if (!win) {
      toast.error("Please allow pop-ups to open the preview.");
      return;
    }

    const tagsHtml = formData.tags
      .map((t) => `<span class="tag">#${escapeHtml(t)}</span>`)
      .join("");

    const words = (formData.content || "")
      .replace(/<[^>]*>/g, " ")
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
    const readTime = `${Math.max(1, Math.round(words / 200))} min read`;

    const clockIcon =
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>';

    win.document.open();
    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(formData.title) || "Blog preview"}</title>
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/ckeditor5@48.2.0/dist/ckeditor5-content.css" />
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; background: #fff; color: #1f2937;
      font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; }
    .hero { background: linear-gradient(to bottom right, #111827, #1f2937, #111827);
      padding: 96px 24px 80px; }
    .hero-inner { max-width: 896px; margin: 0 auto; }
    .top { display: flex; align-items: center; gap: 24px; margin-bottom: 24px; }
    .cat { display: inline-block; background: #b32126; color: #fff; font-size: 14px;
      font-weight: 600; padding: 6px 16px; border-radius: 999px; }
    .hero h1 { color: #fff; font-weight: 800; line-height: 1.1; margin: 0 0 24px; font-size: 48px; }
    .meta { display: flex; flex-wrap: wrap; gap: 24px; color: #d1d5db; font-size: 15px; }
    .meta span { display: inline-flex; align-items: center; gap: 8px; }
    .image-wrap { max-width: 1152px; margin: -32px auto 0; padding: 0 24px; }
    .image-wrap img { width: 100%; height: 384px; object-fit: cover; border-radius: 16px;
      box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); display: block; }
    .content-wrap { max-width: 1152px; margin: 0 auto; padding: 64px 24px; }
    .ck-content { padding: 0; font-size: 18px; line-height: 1.8; color: #374151; }
    .ck-content h1, .ck-content h2, .ck-content h3 { color: #111827; font-weight: 700; }
    .ck-content a { color: #b32126; }
    .ck-content img { max-width: 100%; height: auto; border-radius: 12px; }
    .ck-content blockquote { border-left: 4px solid #b32126; background: #f9fafb;
      padding: 4px 20px; border-radius: 0 8px 8px 0; color: #374151; }
    .footer { max-width: 1152px; margin: 0 auto; padding: 0 24px 80px; }
    .tags { display: flex; flex-wrap: wrap; gap: 8px; padding-top: 32px; border-top: 1px solid #e5e7eb; }
    .tag { background: #f3f4f6; color: #4b5563; font-size: 14px; padding: 4px 12px; border-radius: 999px; }
  </style>
</head>
<body>
  <div class="hero">
    <div class="hero-inner">
      ${formData.category ? `<div class="top"><span class="cat">${escapeHtml(formData.category)}</span></div>` : ""}
      <h1>${escapeHtml(formData.title) || "Untitled article"}</h1>
      <div class="meta">
        <span>${clockIcon} ${readTime}</span>
      </div>
    </div>
  </div>
  ${imagePreview ? `<div class="image-wrap"><img src="${imagePreview}" alt="" /></div>` : ""}
  <div class="content-wrap">
    <div class="ck-content">${formData.content || ""}</div>
  </div>
  ${formData.tags.length ? `<div class="footer"><div class="tags">${tagsHtml}</div></div>` : ""}
</body>
</html>`);
    win.document.close();
  };

  const headerActions = (
    <>
      <button
        type="button"
        onClick={handlePreview}
        disabled={!hasPreview}
        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40"
      >
        <Eye className="h-4 w-4" />
        Preview
      </button>
      <button
        type="button"
        onClick={() => navigate("/admin/blogs")}
        className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="blog-form"
        disabled={saving}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#b32126] hover:bg-[#8f1a1f] shadow-sm shadow-[#b32126]/20 disabled:opacity-60 transition-colors"
      >
        {saving ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Save className="h-4 w-4" />
        )}
        {saving ? "Saving…" : "Save Blog"}
      </button>
    </>
  );

  return (
    <AdminLayout
      title="Add Blog"
      subtitle="Create and publish a new article"
      actions={headerActions}
    >
      <form id="blog-form" onSubmit={handleSubmit}>
        {/* Two equal columns */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* ---------- Left: article details ---------- */}
          <Card title="Article Details">
            <div className="space-y-5">
              {/* Title */}
              <div>
                <FieldLabel icon={Type} htmlFor="title">
                  Title
                </FieldLabel>
                <input
                  id="title"
                  type="text"
                  name="title"
                  placeholder="Enter the blog title"
                  value={formData.title}
                  onChange={handleChange}
                  maxLength={TITLE_MAX}
                  className={fieldClass}
                />
              </div>

              {/* Slug */}
              <div>
                <FieldLabel icon={Link2} htmlFor="slug">
                  Slug
                </FieldLabel>
                <div className="flex items-stretch rounded-md border border-gray-200 bg-gray-50 focus-within:border-[#b32126] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#b32126]/15 transition overflow-hidden">
                  <span className="flex items-center px-3 text-sm text-gray-400 border-r border-gray-200 select-none">
                    /blogs/
                  </span>
                  <input
                    id="slug"
                    type="text"
                    name="slug"
                    placeholder="url-friendly-slug"
                    value={formData.slug}
                    onChange={handleSlugChange}
                    maxLength={SLUG_MAX}
                    className="flex-1 bg-transparent px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <FieldLabel icon={Tag} htmlFor="category">
                  Category
                </FieldLabel>
                <Select
                  value={formData.category}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, category: value }))
                  }
                >
                  <SelectTrigger
                    id="category"
                    className="h-auto w-full rounded-md border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm data-[placeholder]:text-gray-400 focus:border-[#b32126] focus:bg-white focus:ring-2 focus:ring-[#b32126]/15"
                  >
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          {/* ---------- Right: image + tags ---------- */}
          <Card title="Media & Tags">
            <div className="space-y-5">
              {/* Featured image — choose from system */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />

                {/* Upload area — hidden once an image URL is entered */}
                {!formData.imageUrl.trim() &&
                  (imagePreview ? (
                    <div className="relative group rounded-md overflow-hidden border border-gray-200">
                      <img
                        src={imagePreview}
                        alt="preview"
                        className="h-36 w-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="px-3 py-1.5 rounded-md text-xs font-medium bg-white text-gray-800"
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          onClick={removeImage}
                          className="px-3 py-1.5 rounded-md text-xs font-medium bg-white text-[#b32126]"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full flex h-36 flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 hover:border-[#b32126] hover:text-[#b32126] hover:bg-[#b32126]/5 transition-colors"
                    >
                      <Upload className="h-7 w-7" />
                      <span className="text-sm font-medium">
                        Click to choose an image
                      </span>
                      <span className="text-xs">PNG, JPG up to a few MB</span>
                    </button>
                  ))}

                {/* Preview for an image URL */}
                {formData.imageUrl.trim() && (
                  <div className="relative group rounded-md overflow-hidden border border-gray-200">
                    <img
                      src={formData.imageUrl.trim()}
                      alt="preview"
                      className="h-36 w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <button
                        type="button"
                        onClick={removeImage}
                        className="px-3 py-1.5 rounded-md text-xs font-medium bg-white text-[#b32126]"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}

                {formData.image && (
                  <p className="mt-2 truncate text-xs text-gray-500">
                    {formData.image.name}
                  </p>
                )}

                <p className="mt-2 text-xs text-gray-400">
                  Use a fixed 16:9 ratio image (e.g. 1200 × 675px) for best
                  results.
                </p>

                {/* Image URL — hidden once a file is uploaded */}
                {!formData.image && (
                  <div className="mt-3">
                    <FieldLabel icon={Link2} htmlFor="imageUrl">
                      Image URL
                    </FieldLabel>
                    <input
                      id="imageUrl"
                      type="url"
                      name="imageUrl"
                      placeholder="https://example.com/image.jpg"
                      value={formData.imageUrl}
                      onChange={handleImageUrlChange}
                      className={fieldClass}
                    />
                  </div>
                )}
              </div>

              {/* Tags */}
              <div>
                <FieldLabel icon={Tags} htmlFor="tags">
                  Tags
                </FieldLabel>
                <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-2 focus-within:border-[#b32126] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#b32126]/15 transition">
                  {formData.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 rounded bg-[#b32126]/10 px-2 py-0.5 text-xs font-medium text-[#b32126]"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="hover:text-[#8f1a1f]"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    id="tags"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    onBlur={() => addTag(tagInput)}
                    maxLength={TAG_MAX}
                    placeholder={formData.tags.length ? "" : "Add tags…"}
                    className="flex-1 min-w-[80px] bg-transparent py-0.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  Press Enter or comma to add
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* ---------- Content ---------- */}
        <div className="mt-6">
          <Card title="Content">
            <div className="rounded-md border border-gray-200 bg-gray-50 overflow-hidden focus-within:border-[#b32126] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#b32126]/15 transition">
              <RichTextEditor
                value={formData.content}
                placeholder="Write your story…"
                onChange={(html) =>
                  setFormData((prev) => ({
                    ...prev,
                    content: isEmptyHtml(html) ? "" : html,
                  }))
                }
              />
            </div>
          </Card>
        </div>

      </form>
    </AdminLayout>
  );
};

export default BlogPageAdd;